// Other Plugins
import { logManager } from '../../logs'

// Libraries
import { all, call } from 'redux-saga/effects'
import { isEmpty, isUndefined } from 'lodash/fp'

const log = logManager.getLogger('CALLSTACK')

/**
 * Performs the webRTC session functions associated rolling back the local portion
 *  of a "hold" (inactive media) offer
 *
 * Responsibilities:
 *  1. Revert the direction of the local and remote transceivers to "sendrecv"
 *  2. Rollback the local description SDP offer
 * @method rollbackLocalHoldOperation
 * @param {Object} deps
 * @param {Object} deps.webRTC      The WebRTC stack.
 * @param {string} sessionId the local webRTC session id, used to lookup the session object
 * @return {Object} offer object containing a Session Description Protocol
 */
export function * rollbackLocalHoldOperation (deps, sessionId) {
  const { webRTC } = deps
  const session = yield call([webRTC.sessionManager, 'get'], sessionId)

  if (!session) {
    log.debug(`WebRTC session ${sessionId} not found.`)
    return
  }

  // Set the direction of the transceivers back to 'sendrecv'
  yield call([session, 'setLocalAndRemoteTransceiversDirection'], {
    audio: 'sendrecv',
    video: 'sendrecv'
  })

  // Rollback the local offer
  let offer
  try {
    offer = yield call([session, 'rollbackLocalDescription'])
  } catch (error) {
    log.debug('Failed to rollback local description offer SDP:', error)
    return {
      error
    }
  }

  return { offer }
}

/**
 * Performs the webRTC session functions associated rolling back the local portion
 *  of an "unhold" (full media) offer
 *
 * Responsibilities:
 *  1. Revert the direction of the local and remote transceivers to "inactive"
 *  2. Rollback the local description SDP offer
 * @method rollbackLocalUnholdOperation
 * @param {Object} deps
 * @param {Object} deps.webRTC      The WebRTC stack.
 * @param {string} sessionId the local webRTC session id, used to lookup the session object
 * @return {Object} offer object containing a Session Description Protocol
 */
export function * rollbackLocalUnholdOperation (deps, sessionId) {
  const { webRTC } = deps
  const session = yield call([webRTC.sessionManager, 'get'], sessionId)

  if (!session) {
    log.debug(`WebRTC session ${sessionId} not found.`)
    return
  }

  // Set the direction of the transceivers back to 'inactive'
  yield call([session, 'setLocalAndRemoteTransceiversDirection'], {
    audio: 'inactive',
    video: 'inactive'
  })

  // Rollback the local offer
  let offer
  try {
    offer = yield call([session, 'rollbackLocalDescription'])
  } catch (error) {
    log.debug('Failed to rollback local description offer SDP:', error)
    return {
      error
    }
  }

  return { offer }
}

/**
 * Performs the webRTC session functions associated rolling back the local portion
 *  of an "Add Media" offer
 *
 * Responsibilities:
 *  1. Remove and cleanup the tracks added to the peer
 *  2. Rollback the local description SDP offer
 * @method rollbackLocalAddMedia
 * @param {Object} deps
 * @param {Object} deps.webRTC  The WebRTC stack.
 * @param {string} sessionId    The local webRTC session id, used to lookup the session object
 * @param {Array}  medias       A list of Media state objects
 * @return {Object} offer object containing a Session Description Protocol
 */
export function * rollbackLocalAddMedia (deps, sessionId, medias) {
  const { webRTC } = deps
  const session = yield call([webRTC.sessionManager, 'get'], sessionId)

  if (!session) {
    log.debug(`WebRTC session ${sessionId} not found.`)
    return
  }

  // Remove the new local media(s) that were added
  let tracks = []
  medias.forEach(media => {
    tracks = tracks.concat(media.tracks.map(track => track.id))
  })

  // Get the tracks that we want to remove
  const localTracksToRemove = yield call([webRTC.track, 'getTracks'], tracks)
  // Get the indexes of undefined tracks.
  const invalidIndexes = localTracksToRemove.reduce((acc, cur, ind) => {
    return isUndefined(cur) ? acc.concat(ind) : acc
  }, [])
  // Get the track IDs of those indexes.
  const invalidTracks = invalidIndexes.map(ind => tracks[ind])

  if (!isEmpty(invalidTracks)) {
    const message = `The following tracks could not be found for rollback: ${invalidTracks.join(', ')}`
    log.debug(message)
  }

  // Removes tracks from peer (Will stop tracks from being sent to remote participant).
  // Does NOT end the tracks.
  yield call([session, 'removeTracks'], tracks)

  // Ends the tracks.
  // Clean-up the local tracks.
  yield all(localTracksToRemove.map(track => call([track, 'stop'])))

  // Rollback the local offer
  let offer
  try {
    offer = yield call([session, 'rollbackLocalDescription'])
  } catch (error) {
    log.debug('Failed to rollback local description offer SDP:', error)
    return {
      error
    }
  }

  return { offer }
}

/**
 * Performs the webRTC session functions associated rolling back the local portion
 *  of a "Remove Media" offer.
 *
 * Unlike other rollback operations, for a remove media operation we will not try and re-add
 *  the removed tracks since the application's intention was to stop the media.
 *
 * Responsibilities:
 *  1. Rollback the local description SDP offer
 * @method rollbackLocalRemoveMedia
 * @param {Object} deps
 * @param {Object} deps.webRTC  The WebRTC stack.
 * @param {string} sessionId    The local webRTC session id, used to lookup the session object
 * @return {Object} offer object containing a Session Description Protocol
 */
export function * rollbackLocalRemoveMedia (deps, sessionId) {
  const { webRTC } = deps
  const session = yield call([webRTC.sessionManager, 'get'], sessionId)

  if (!session) {
    log.debug(`WebRTC session ${sessionId} not found.`)
    return
  }

  // Rollback the local offer
  let offer
  try {
    offer = yield call([session, 'rollbackLocalDescription'])
  } catch (error) {
    log.debug('Failed to rollback local description offer SDP:', error)
    return {
      error
    }
  }

  return { offer }
}
